/**
 * FooocusStyleOptions - Style configuration panel for Fooocus AI image generation.
 * Supports Auto mode (AI selects styles from description) and Manual mode (visual gallery).
 */

import { Sparkles, Wand2, Palette, Layers } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { StyleSelector } from '../../image/StyleSelector';
import { ImageModelSelector } from '../../image/ImageModelSelector';
import type { FooocusStyleMode } from '../MediaSourceSelector';
import {
  FOOOCUS_UI_PRESET_OPTIONS,
  MAX_FOOOCUS_STYLES,
  isPresetActive,
} from '@/constants/fooocusPresets';
import { SegmentedPicker } from '../../common/SegmentedPicker';
import type { SegmentOption } from '../../common/SegmentedPicker';

/** Auto mode style presets for quick selection */
const AUTO_PRESETS = [
  { label: 'Cinematic', description: 'cinematic dramatic moody' },
  { label: 'Realistic', description: 'realistic photographic detailed' },
  { label: 'Anime', description: 'anime vibrant colorful' },
  { label: 'Fantasy', description: 'fantasy magical ethereal' },
  { label: 'Dark', description: 'dark moody atmospheric horror' },
  { label: 'Futuristic', description: 'futuristic sci-fi cyberpunk neon' },
  { label: 'Nature', description: 'nature landscape scenic beautiful' },
  { label: 'Professional', description: 'professional clean corporate' },
];

interface FooocusStyleOptionsProps {
  styleMode: FooocusStyleMode;
  styles: string[];
  styleDescription: string;
  fooocusModel?: string;
  onStyleModeChange: (mode: FooocusStyleMode) => void;
  onStylesChange: (styles: string[]) => void;
  onStyleDescriptionChange: (description: string) => void;
  onFooocusModelChange?: (model: string) => void;
  disabled: boolean;
  /**
   * Whether to render the style controls (mode toggle, presets, auto/manual
   * gallery). Driven by `PROVIDER_FEATURES[provider].supportsStyles` at the call
   * site so this subpanel matches the model-aware gating used by ImageConfigForm
   * / ImageGeneratorPanel. Defaults to true (Fooocus supports styles).
   */
  showStyles?: boolean;
  /**
   * Whether to render the checkpoint model selector. Driven by
   * `PROVIDER_FEATURES[provider].supportsModels`. Defaults to true.
   */
  showModel?: boolean;
}

export function FooocusStyleOptions({
  styleMode,
  styles,
  styleDescription,
  fooocusModel,
  onStyleModeChange,
  onStylesChange,
  onStyleDescriptionChange,
  onFooocusModelChange,
  disabled,
  showStyles = true,
  showModel = true,
}: FooocusStyleOptionsProps) {
  const { t } = useTranslation();

  const modeOptions: SegmentOption[] = [
    {
      value: 'auto',
      label: t('fooocus.mode.auto', { defaultValue: 'Auto (AI Selects)' }),
      description: t('fooocus.mode.autoDesc', {
        defaultValue: 'AI picks matching styles from your description',
      }),
      iconNode: <Wand2 className="h-4 w-4" />,
    },
    {
      value: 'manual',
      label: t('fooocus.mode.manual', { defaultValue: 'Manual Select' }),
      description: t('fooocus.mode.manualDesc', {
        defaultValue: 'Browse and pick styles from the visual gallery',
      }),
      iconNode: <Palette className="h-4 w-4" />,
    },
  ];

  return (
    <div className="border border-white/10 rounded-lg p-4 space-y-4 bg-black/20">
      <h4 className="text-sm font-medium text-white/80 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary-400 flex-shrink-0" />
        <span className="break-words">
          {t('imageSource.fooocus.title', { defaultValue: 'Fooocus AI Style Settings' })}
        </span>
      </h4>

      {/* Checkpoint Model Selector — comes first, matching ComfyUISDOptions'
          field order so the two provider panels read as the same layout. */}
      {showModel && onFooocusModelChange && (
        <ImageModelSelector
          value={fooocusModel}
          onChange={onFooocusModelChange}
          provider="fooocus"
          disabled={disabled}
        />
      )}

      {showStyles && (
        <>
          {/* Mode Toggle: Auto vs Manual */}
          <SegmentedPicker
            options={modeOptions}
            value={styleMode}
            onChange={(val) => !disabled && onStyleModeChange(val as FooocusStyleMode)}
            size="md"
            layout="equal"
            disabled={disabled}
          />

          {/* Fooocus preset shortcuts: set styles -> preset.default_styles, switch to manual */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
              <label className="text-sm font-medium text-white/70 flex items-center gap-1">
                <Layers className="h-4 w-4 flex-shrink-0 text-primary-400" />
                {t('fooocus.checkpointPreset.label', {
                  defaultValue: 'Fooocus Preset (Base quality)',
                })}
              </label>
              <span className="text-xs text-white/40">
                {t('fooocus.checkpointPreset.hint', {
                  defaultValue: 'One click sets the base styles for this checkpoint',
                })}
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {FOOOCUS_UI_PRESET_OPTIONS.map((preset) => {
                const active = isPresetActive(preset, styles);
                return (
                  <button
                    key={preset.name}
                    type="button"
                    onClick={() => {
                      if (disabled) return;
                      onStylesChange([...preset.styles]);
                      if (styleMode !== 'manual') onStyleModeChange('manual');
                    }}
                    disabled={disabled}
                    title={preset.styles.join(' + ')}
                    className={[
                      'px-2 py-1 text-xs rounded-full transition-colors',
                      active
                        ? 'bg-primary-600 text-white border border-primary-500 shadow-sm'
                        : 'bg-white/5 text-white/60 hover:bg-primary-900/30 hover:text-primary-300 border border-white/10',
                      disabled ? 'opacity-50 cursor-not-allowed' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Auto Mode: Style description with presets */}
          {styleMode === 'auto' && (
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                <label className="text-sm font-medium text-white/70">
                  {t('fooocus.auto.label', { defaultValue: 'Style Description' })}
                </label>
                <span className="text-xs text-white/40">
                  {t('fooocus.auto.hint', { defaultValue: 'AI will select matching styles' })}
                </span>
              </div>

              {/* Preset chips */}
              <div className="flex flex-wrap gap-1">
                {AUTO_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => !disabled && onStyleDescriptionChange(preset.description)}
                    disabled={disabled}
                    className={[
                      'px-2 py-1 text-xs rounded-full transition-colors',
                      styleDescription === preset.description
                        ? 'bg-primary-900/40 text-primary-200 border border-primary-500/50'
                        : 'bg-white/5 text-white/60 hover:bg-primary-900/20 hover:text-primary-300 border border-white/10',
                      disabled ? 'opacity-50 cursor-not-allowed' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Text input */}
              <input
                type="text"
                value={styleDescription}
                onChange={(e) => !disabled && onStyleDescriptionChange(e.target.value)}
                disabled={disabled}
                placeholder={t('fooocus.auto.placeholder', {
                  defaultValue: 'e.g., cinematic dark moody, anime vibrant...',
                })}
                className={[
                  'w-full px-3 py-2 border border-white/10 rounded-lg text-sm',
                  'bg-black/20 text-white',
                  'focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none',
                  disabled ? 'opacity-50 cursor-not-allowed' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
              <p className="text-xs text-white/40">
                {t('fooocus.auto.info', {
                  defaultValue:
                    'The AI will analyze your description and select the best matching styles automatically.',
                })}
              </p>
            </div>
          )}

          {/* Manual Mode: Visual style gallery with thumbnails */}
          {styleMode === 'manual' && (
            <div className="space-y-2">
              <p className="text-xs text-white/40">
                {t('fooocus.manual.maxHint', {
                  defaultValue:
                    'Pick up to {{count}} styles. A preset already counts toward that limit — add thematic styles (Cinematic, Hyperrealism, ...) to fill the rest.',
                  count: MAX_FOOOCUS_STYLES,
                })}
              </p>
              <StyleSelector
                selectedStyles={styles}
                onChange={onStylesChange}
                maxStyles={MAX_FOOOCUS_STYLES}
                disabled={disabled}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
